import type { PrefetchFile } from './api'
import { cacheMessage, shellFiles, type CacheReport } from './screenCache'

/** The scope a screen's worker controls: its own kind of address, never the staff app. */
export function workerScope(pathname: string): string | null {
  const m = /^\/(display|board|kiosk)\//.exec(pathname)
  return m ? `/${m[1]}/` : null
}

/** Registers the screens' worker, for an addressed screen in a browser that has them; others stream as before. */
export function startScreenWorker(): void {
  const scope = workerScope(window.location.pathname)
  if (!scope || !('serviceWorker' in navigator) || !window.isSecureContext) return
  // A first visit loaded before the worker took the page: once it does, hand it what was loaded.
  navigator.serviceWorker.addEventListener('controllerchange', keepShell)
  navigator.serviceWorker.register('/screen-sw.js', { scope, type: 'module' }).catch(() => undefined)
}

/** Asks the worker to keep this page and what it loaded, so a reload offline still draws. */
function keepShell(): void {
  const controller = navigator.serviceWorker?.controller
  if (!controller) return
  const loaded = performance.getEntriesByType('resource').map((e) => e.name)
  controller.postMessage({ type: 'shell', urls: shellFiles(window.location.href, loaded, window.location.origin) })
}

/** Hands the worker one screen's list from the layout answer, as the addresses it fetches its files from. */
export function holdFiles(screen: string, files: readonly PrefetchFile[], url: (assetId: string) => string): void {
  const controller = typeof navigator === 'undefined' ? null : navigator.serviceWorker?.controller
  if (!controller) return
  const listed = files.map((f) => ({ url: url(f.id), bytes: f.bytes, from: f.from }))
  controller.postMessage({ type: 'hold', screen, files: listed })
  keepShell()
}

/** Hands each cache report the worker posts about this screen to report; returns the unsubscribe. */
export function watchCache(screen: string, report: (r: CacheReport) => void): () => void {
  const container = typeof navigator === 'undefined' ? undefined : navigator.serviceWorker
  if (!container) return () => {}
  const on = (e: MessageEvent) => {
    const r = cacheMessage(e.data, screen)
    if (r) report(r)
  }
  container.addEventListener('message', on)
  return () => container.removeEventListener('message', on)
}
